import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MagnifyingGlass, CircleNotch, LinkedinLogo, EnvelopeSimple, X } from "@phosphor-icons/react";

function Person({ siren, p, reload }) {
  const [busy, setBusy] = useState(false);
  const findEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/companies/${siren}/people/${p.id}/find-email`);
      toast[data.found ? "success" : "info"](data.found ? `Email trouvé : ${data.contact?.email}` : "Aucun email professionnel trouvé pour cette personne.");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const dismiss = async () => { await api.delete(`/admin/leads/companies/${siren}/people/${p.id}`).catch(() => {}); reload(); };
  return (
    <div className="flex items-center justify-between gap-2 text-xs border border-border/70 rounded-md px-2.5 py-2" data-testid={`lead-person-${p.id}`}>
      <div className="min-w-0">
        <div className="text-fg font-medium truncate">{p.name} <span className="text-muted font-normal">· {p.role}</span></div>
        <div className="text-[10px] text-muted">{p.source}{p.email_searched ? " · email non trouvé" : ""}</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {p.linkedin && <a href={p.linkedin} target="_blank" rel="noreferrer" className="p-1 text-muted hover:text-accent" title="Profil LinkedIn"><LinkedinLogo size={14} /></a>}
        {!p.email_searched && (
          <button onClick={findEmail} disabled={busy} title="Recherche Prospeo (1 crédit)" data-testid={`lead-person-find-email-${p.id}`}
            className="flex items-center gap-1 text-[11px] border border-border rounded px-2 py-0.5 hover:border-accent/40 disabled:opacity-60">
            {busy ? <CircleNotch size={11} className="animate-spin" /> : <EnvelopeSimple size={11} />} Chercher l'email
          </button>
        )}
        <button onClick={dismiss} className="p-1 text-muted hover:text-destructive" title="Ignorer" data-testid={`lead-person-dismiss-${p.id}`}><X size={12} /></button>
      </div>
    </div>
  );
}

export default function LeadContactFinder({ siren, people, searches, disabled, reload }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/companies/${siren}/find-contacts`);
      toast.success(`${data.added.length} contact(s) avec email ajouté(s) · ${data.people_without_email.length} personne(s) sans email · coût ${data.cost_usd} $`);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const last = (searches || []).slice(-1)[0];
  return (
    <div className="space-y-2 mt-3" data-testid="lead-contact-finder">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={run} disabled={busy || disabled} data-testid="lead-contact-find-btn"
          title="Exa (site officiel, dirigeants, emails publiés) puis Prospeo en secours pour les emails manquants"
          className="flex items-center gap-1.5 text-xs bg-accent text-black font-semibold rounded px-3 py-1.5 disabled:opacity-50 hover-lift">
          {busy ? <CircleNotch size={12} className="animate-spin" /> : <MagnifyingGlass size={12} weight="bold" />} {busy ? "Recherche Exa → Prospeo…" : "Trouver les contacts"}
        </button>
        {last && <span className="text-[10px] text-muted" data-testid="lead-contact-last-search">Dernière recherche : {new Date(last.at).toLocaleString("fr-FR")} · {last.added} ajouté(s) · {last.prospeo_calls} appel(s) Prospeo · {last.cost_usd} $</span>}
      </div>
      {(people || []).length > 0 && (
        <div className="space-y-1.5" data-testid="lead-people-found">
          <div className="text-[11px] text-muted">Personnes identifiées sans email ({people.length}) — aucun email n'est déduit ni inventé</div>
          {people.map((p) => <Person key={p.id} siren={siren} p={p} reload={reload} />)}
        </div>
      )}
    </div>
  );
}
